import type { ParentSharedStudent, TuitionMonthlyFee, TuitionPayment } from '@/types';
import { formatDate, formatNumber } from '@/lib/datetime';
import {
  cycleDueDate,
  cycleMonthLabel,
  dueDayLabel,
  excludedCycleKeysOf,
  isCycleExcluded,
  monthlyFeeAt,
  paidCycleKeysOf,
  parseCycleKey,
  resolveTuitionCycles,
  todayKey,
  type CyclePaymentStatus,
} from '@/lib/tuition';
import ParentTuitionMonthlyFee from '@/pages/share/components/ParentTuitionMonthlyFee';
import ParentTuitionPaymentHistory from '@/pages/share/components/ParentTuitionPaymentHistory';

interface ParentTuitionCardProps {
  student: ParentSharedStudent;
  monthlyFees: TuitionMonthlyFee[];
  payments: TuitionPayment[];
}

const STATUS_META: Record<
  CyclePaymentStatus,
  { label: string; badge: string; icon: string }
> = {
  paid: { label: '결제완료', badge: 'bg-secondary-100 text-secondary-900', icon: 'ri-checkbox-circle-line' },
  overdue: { label: '미납', badge: 'bg-accent-100 text-accent-800', icon: 'ri-error-warning-line' },
  upcoming: { label: '결제 예정', badge: 'bg-background-200 text-foreground-700', icon: 'ri-time-line' },
  excluded: { label: '회차 제외', badge: 'bg-background-200 text-foreground-600', icon: 'ri-forbid-line' },
};

export default function ParentTuitionCard({ student, monthlyFees, payments }: ParentTuitionCardProps) {
  const dueDay = student.tuition_due_day || 0;
  const dueOffset = student.tuition_due_month_offset ?? 0;
  const baseFee = student.tuition_fee || 0;

  const paidKeys = paidCycleKeysOf(payments, dueOffset);
  const excludedKeys = excludedCycleKeysOf(monthlyFees);
  const resolved = resolveTuitionCycles(student.tuition_due_day, paidKeys, excludedKeys, dueOffset);
  const status: CyclePaymentStatus =
    resolved?.status ?? (student.tuition_last_paid_at ? 'paid' : 'upcoming');
  const meta = STATUS_META[status];

  const today = todayKey();
  const [curYear, curMonth] = today.split('-').map(Number);
  const currentFee = monthlyFeeAt(monthlyFees, baseFee, curYear, curMonth);
  const currentExcluded = isCycleExcluded(monthlyFees, curYear, curMonth);

  const collectKey = resolved?.collectKey ?? null;
  const collectDue = collectKey && dueDay ? cycleDueDate(collectKey, dueDay, dueOffset) : null;
  const collectFee = collectKey
    ? monthlyFeeAt(
        monthlyFees,
        baseFee,
        parseCycleKey(collectKey).year,
        parseCycleKey(collectKey).month,
      )
    : 0;

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="flex flex-wrap items-center gap-2 text-sm font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
          <i className="ri-wallet-3-line"></i>
        </span>
        교습비
        <span
          className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.badge}`}
        >
          <i className={meta.icon}></i>
          {meta.label}
        </span>
      </h2>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-primary-50 p-4">
          <p className="text-xs text-foreground-500">이번 달 교습비</p>
          <p className="mt-1 text-xl font-bold text-primary-700">
            {currentExcluded ? '휴원·면제' : currentFee ? `${formatNumber(currentFee)}원` : '미입력'}
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-100 p-4">
          <p className="text-xs text-foreground-500">결제일</p>
          <p className="mt-1 text-xl font-bold text-foreground-900">
            {dueDayLabel(student.tuition_due_day, student.tuition_due_month_offset)}
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-accent-50 p-4">
          <p className="text-xs text-foreground-500">
            {status === 'overdue' ? '미납 결제일' : '다음 결제일'}
          </p>
          <p className="mt-1 text-xl font-bold text-accent-700">{formatDate(collectDue)}</p>
          {collectKey && (
            <p className="mt-0.5 text-xs text-foreground-500">
              {cycleMonthLabel(collectKey)} 회차
              {collectFee > 0 ? ` · ${formatNumber(collectFee)}원` : ''}
            </p>
          )}
        </div>
      </div>

      <ParentTuitionMonthlyFee monthlyFees={monthlyFees} />
      <ParentTuitionPaymentHistory payments={payments} />
    </section>
  );
}